import type { LockOutcome, LockTable } from './entity-locks';
import type { LockHolder } from './message';
import type { AvatarSource } from './peer-avatar';

/** Who holds a lock, as the Design Studio editor sees them. `self` is true when this tab's connection holds it. */
export type BridgeLockHolder = {
  clientId: string;
  tab: string;
  user: { id: number; name: string; avatar?: string };
  self: boolean;
};

/** What `lock`/`takeOver` came back with: whether this tab holds the field now, and who does. */
export type LockResult = { acquired: boolean; holder: BridgeLockHolder | null };

export type BridgeLocks = ReadonlyMap<string, BridgeLockHolder>;

export function toBridgeHolder(holder: LockHolder, selfClient: string | null, avatars: AvatarSource): BridgeLockHolder {
  const user: BridgeLockHolder['user'] = { id: holder.id, name: holder.name };
  const avatar = avatars.lookup(holder.id);
  if (avatar !== undefined) user.avatar = avatar;
  return { clientId: holder.client, tab: holder.tab, user, self: holder.client === selfClient };
}

export function toBridgeLocks(table: LockTable, selfClient: string | null, avatars: AvatarSource): BridgeLocks {
  avatars.ensure([...table.values()].map((holder) => holder.id));
  return new Map([...table].map(([field, holder]) => [field, toBridgeHolder(holder, selfClient, avatars)]));
}

export function toLockResult(outcome: LockOutcome, selfClient: string | null, avatars: AvatarSource): LockResult {
  const { acquired, holder } = outcome;
  return { acquired, holder: holder === null ? null : toBridgeHolder(holder, selfClient, avatars) };
}

/** Everything a bridge listener can see about a holder, so an unchanged table isn't re-sent. */
const holderKey = ({ clientId, tab, user, self }: BridgeLockHolder) =>
  JSON.stringify([clientId, tab, user.id, user.name, user.avatar, self]);

export const sameLocks = (a: BridgeLocks, b: BridgeLocks) =>
  a.size === b.size &&
  [...a].every(([field, holder]) => {
    const other = b.get(field);
    return other !== undefined && holderKey(holder) === holderKey(other);
  });
